'use client';

import { useParams, useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from 'baseui/spinner';
import { isNil } from 'lodash';
import { BaseLayout } from 'structure/Layouts/SFILayouts/BaseLayout/BaseLayout';
import ProjectOverview from 'structure/multi-asset-staking/components/ProjectOverview';
import { ModalStake } from 'structure/staking/containers/ModalStake';
import { ModalStakeClaim } from 'structure/staking/containers/ModalStakeClaim';
import { ModalStakeExtend } from 'structure/staking/containers/ModalStakeExtend';
import { ModalStakeMore } from 'structure/staking/containers/ModalStakeMore';
import { ModalStakeWithdraw } from 'structure/staking/containers/ModalStakeWithdraw';
import { StakingPoolList } from 'structure/staking/containers/StakingPoolList';
import { UserReputationInfo } from 'structure/staking/containers/UserReputationInfo';
import { useStakingProjectInfo } from 'structure/staking/hooks/useStakingAPI';
import { ModalName, ModalNames } from 'structure/staking/types';
import { Typography } from '@dapp/multi-lib/sdaoui/Typography';

export function StakingProjectClient() {
  const params = useParams<{ id: string }>();
  const id = params?.id ?? '';
  const router = useRouter();
  const [modal, setModal] = useState<{ name: string; poolId: number } | null>(
    null
  );
  const { t } = useTranslation('common');

  const { data: project, isFetching } = useStakingProjectInfo({
    tokenSymbol: id,
    query: { enabled: Boolean(id) },
  });

  const handleOpenModal = (modalName: string, poolId: number) => {
    setModal({ name: modalName, poolId });
  };

  const handleCloseModal = (isOpen: boolean) => {
    if (!isOpen) setModal(null);
  };

  const isModalOpen = (modalName: ModalName) =>
    null !== modal && modalName === modal.name;

  useEffect(() => {
    if (
      !isFetching &&
      (isNil(project?.tokenSymbol) || project?.tokenSymbol === 'GNESS')
    ) {
      router.push('/staking');
    }
  }, [isFetching, project?.tokenSymbol, router]);

  if (
    isFetching ||
    isNil(project?.tokenSymbol) ||
    project?.tokenSymbol === 'GNESS'
  ) {
    return (
      <div className='m-auto w-full p-8'>
        <Spinner $size='14px' $borderWidth='3px' />
      </div>
    );
  }

  return (
    <BaseLayout>
      <div className='mb-16 mt-10 flex flex-col px-0 max-ngmd:px-2'>
        <div className='flex flex-row justify-between gap-8 max-lg:flex-col md:gap-4'>
          <ProjectOverview project={project} />
          {project?.tokenSymbol === 'SFI' && <UserReputationInfo />}
        </div>
        <Typography className='mb-6 mt-16 h-11 text-2xl font-bold text-white'>
          {project?.title} {t('STAKING.POOLS')}
        </Typography>
        <StakingPoolList
          tokenSymbol={project?.tokenSymbol}
          project={project}
          onOpenModal={handleOpenModal}
        />
      </div>
      <div>
        {modal ? (
          <>
            {isModalOpen(ModalNames.STAKE) && (
              <ModalStake
                poolId={modal.poolId}
                open={isModalOpen(ModalNames.STAKE)}
                onOpenChange={handleCloseModal}
              />
            )}
            {isModalOpen(ModalNames.STAKE_MORE) && (
              <ModalStakeMore
                poolId={modal.poolId}
                open={isModalOpen(ModalNames.STAKE_MORE)}
                onOpenChange={handleCloseModal}
              />
            )}
            {isModalOpen(ModalNames.EXTEND) && (
              <ModalStakeExtend
                poolId={modal.poolId}
                open={isModalOpen(ModalNames.EXTEND)}
                onOpenChange={handleCloseModal}
              />
            )}
            {isModalOpen(ModalNames.WITHDRAW) && (
              <ModalStakeWithdraw
                poolId={modal.poolId}
                open={isModalOpen(ModalNames.WITHDRAW)}
                onOpenChange={handleCloseModal}
              />
            )}
            {isModalOpen(ModalNames.CLAIM) && (
              <ModalStakeClaim
                poolId={modal.poolId}
                open={isModalOpen(ModalNames.CLAIM)}
                onOpenChange={handleCloseModal}
              />
            )}
          </>
        ) : null}
      </div>
    </BaseLayout>
  );
}
